/**
 * Junta o que a planilha diz (etapa, prazo, cliente…) com o que o gestor
 * apontou no app (setor, subsetor, posto, status, prazo próprio, tarefas,
 * peças desmembradas).
 *
 * Quem manda na ETAPA é a planilha (data preenchida = etapa finalizada,
 * `setores.js`). Quem manda no SETOR pode ser o gestor: cada etapa tem um setor
 * que RECEBE os ambientes, mas o backend aceita mover um ambiente para
 * qualquer setor cadastrado. Nesse caso a alocação MANDA e a demanda sai com
 * `movidoAMao: true` mais o setor da planilha ao lado (`setorIdPlanilha` /
 * `setorNomePlanilha`), para a tela mostrar o selo e o gestor entender por que
 * aquele ambiente não está onde a planilha diria.
 *
 * A alocação continua valendo só ENQUANTO O AMBIENTE NÃO ANDA. A linha gravada
 * carrega a etapa em que estava quando foi feita (`etapa_origem`); quando o
 * PPCP digita a data e o ambiente muda de etapa, a alocação vira "antiga" e é
 * ignorada — posto e prazo do Corte não seguem o ambiente para o Bordo, nem
 * quando o gestor o tinha movido à mão.
 */
import { F } from "../../../utils/gerencialFields";
import { isBeforeToday, toDate } from "../../../utils/status";
import { SITUACAO, criarCatalogoSetores } from "../setores";
import { chaveAmbiente, chaveApelido, rotuloAmbiente } from "./chaves";
import { contarPecasForaPorAmbiente } from "./pecas";
import {
  ORIGEM_PRIORIDADE,
  PRIORIDADES,
  STATUS_AMBIENTE,
  prioridadeDerivada,
  tarefaAberta,
} from "./vocabulario";

const LISTA_VAZIA = Object.freeze([]);

function porId(itens) {
  return new Map((itens || []).map((item) => [item.id, item]));
}

function agruparPorAmbiente(itens) {
  const mapa = new Map();
  (itens || []).forEach((item) => {
    if (!item.ambiente_chave) return;
    if (!mapa.has(item.ambiente_chave)) mapa.set(item.ambiente_chave, []);
    mapa.get(item.ambiente_chave).push(item);
  });
  return mapa;
}

// Colaborador inativo não "reclama" o nome da planilha se houver um ativo.
function indexarApelidos(colaboradores) {
  const mapa = new Map();
  (colaboradores || []).forEach((colaborador) => {
    const chave = chaveApelido(colaborador.apelido_planilha);
    if (!chave) return;
    const atual = mapa.get(chave);
    if (!atual || (!atual.ativo && colaborador.ativo)) mapa.set(chave, colaborador);
  });
  return mapa;
}

/**
 * Índices do estado do app para busca por id/chave. Aceita `estado` nulo
 * (antes da primeira leitura): devolve mapas vazios.
 * {setores, subsetores, postos, colaboradores, pecas: Map<id,…>,
 *  alocacoes: Map<chave,…>, tarefasPorAmbiente / pecasPorAmbiente:
 *  Map<chave, …[]> (todas), pecasForaPorAmbiente: Map<chave, número>,
 *  colaboradorPorApelido: Map<chaveApelido,…>}
 */
export function indexarEstado(estado) {
  return {
    setores: porId(estado?.setores),
    subsetores: porId(estado?.subsetores),
    postos: porId(estado?.postos),
    colaboradores: porId(estado?.colaboradores),
    pecas: porId(estado?.pecas),
    alocacoes: new Map((estado?.alocacoes || []).map((a) => [a.chave, a])),
    tarefasPorAmbiente: agruparPorAmbiente(estado?.tarefas),
    pecasPorAmbiente: agruparPorAmbiente(estado?.pecas),
    pecasForaPorAmbiente: contarPecasForaPorAmbiente(estado?.pecas),
    colaboradorPorApelido: indexarApelidos(estado?.colaboradores),
  };
}

function statusSemAlocacao(demanda) {
  return demanda.emProducao ? STATUS_AMBIENTE.EM_PRODUCAO : STATUS_AMBIENTE.AGUARDANDO;
}

function prazoDoAmbiente(alocacao, prazoPlanilha) {
  const doApp = toDate(alocacao?.data_limite);
  if (doApp) return { prazoEfetivo: doApp, origemPrazo: "app" };
  return { prazoEfetivo: prazoPlanilha || null, origemPrazo: prazoPlanilha ? "planilha" : null };
}

function prioridadeDoAmbiente(alocacao, prazoEfetivo, hoje) {
  if (PRIORIDADES.includes(alocacao?.prioridade)) {
    return { prioridade: alocacao.prioridade, origemPrioridade: ORIGEM_PRIORIDADE.APP };
  }
  return {
    prioridade: prioridadeDerivada(prazoEfetivo, hoje),
    origemPrioridade: ORIGEM_PRIORIDADE.PRAZO,
  };
}

function colaboradorDaPlanilha(row, indice) {
  const chave = chaveApelido(row?.[F.MARCENEIRO_RESPONSAVEL]);
  return (chave && indice.colaboradorPorApelido.get(chave)?.id) || null;
}

/**
 * A alocação gravada ainda fala do ambiente de HOJE? O backend manda
 * `etapa_origem` (a etapa em que o ambiente estava quando o gestor apontou):
 * quando o PPCP digita a data e o ambiente anda, a linha deixa de valer
 * sozinha e ele é entregue à sala da etapa nova. Linha antiga (gravada antes
 * da coluna existir) cai no vínculo do próprio setor alocado — que é
 * exatamente o que a v2 comparava, e vale também para a sala ligada a um
 * macro-grupo inteiro.
 */
function alocacaoFalaDaEtapa(gravada, demanda, catalogo) {
  if (!gravada) return false;
  if (gravada.etapa_origem) return gravada.etapa_origem === demanda.etapa;
  return catalogo.cobreEtapa(gravada.setor_id, demanda.etapa);
}

function resolverSetor(demanda, gravada, catalogo) {
  const setorAlocado = gravada ? catalogo.setor(gravada.setor_id) : null;
  const naEtapa = alocacaoFalaDaEtapa(gravada, demanda, catalogo);
  // Obra futura está fora do fluxo: nem o gestor a coloca numa sala.
  const valida =
    Boolean(setorAlocado) &&
    setorAlocado.ativo !== false &&
    naEtapa &&
    demanda.situacao !== SITUACAO.OBRA_FUTURA;
  const setorFinal = valida ? setorAlocado : catalogo.setor(demanda.setorId);
  return {
    alocacao: valida ? gravada : null,
    alocacaoAntiga: Boolean(gravada) && !valida,
    setorId: setorFinal?.id || null,
    setorNome: setorFinal?.nome || "",
    movidoAMao: valida && setorAlocado.id !== demanda.setorId,
  };
}

function situacaoFinal(demanda, setorId) {
  if (demanda.situacao === SITUACAO.OBRA_FUTURA) return SITUACAO.OBRA_FUTURA;
  return setorId ? SITUACAO.SETOR : SITUACAO.SEM_SETOR;
}

function combinarUma(demanda, indice, catalogo, hoje) {
  const chave = chaveAmbiente(demanda.row);
  const gravada = chave ? indice.alocacoes.get(chave) || null : null;
  const lugar = resolverSetor(demanda, gravada, catalogo);
  const { alocacao } = lugar;
  const statusApp = alocacao?.status || statusSemAlocacao(demanda);
  const { prazoEfetivo, origemPrazo } = prazoDoAmbiente(alocacao, demanda.prazo);
  const { prioridade, origemPrioridade } = prioridadeDoAmbiente(alocacao, prazoEfetivo, hoje);
  const tarefas = (chave && indice.tarefasPorAmbiente.get(chave)) || LISTA_VAZIA;

  return {
    ...demanda,
    chaveLinha: demanda.chave,
    chave,
    rotulo: rotuloAmbiente(demanda.row),
    alocacao,
    alocacaoAntiga: lugar.alocacaoAntiga,
    // A linha gravada é uma só por chave, mesmo antiga: toda escrita do
    // ambiente (alocar, ação, prazos) manda ESTA versão, senão é 409 certo.
    versaoAlocacao: gravada?.versao ?? 0,
    situacao: situacaoFinal(demanda, lugar.setorId),
    setorId: lugar.setorId,
    setorNome: lugar.setorNome,
    movidoAMao: lugar.movidoAMao,
    setorIdPlanilha: demanda.setorId,
    setorNomePlanilha: demanda.setorNome,
    subsetorId: alocacao?.subsetor_id || null,
    postoId: alocacao?.posto_id || null,
    statusApp,
    motivoPausa: alocacao?.motivo_pausa || "",
    prazoPlanilha: demanda.prazo || null,
    atrasadaPlanilha: demanda.atrasada,
    emProducaoPlanilha: demanda.emProducao,
    prazo: prazoEfetivo,
    prazoEfetivo,
    origemPrazo,
    prioridade,
    origemPrioridade,
    prevista: toDate(alocacao?.data_prevista),
    atrasada: Boolean(prazoEfetivo) && isBeforeToday(prazoEfetivo, hoje),
    emProducao: statusApp === STATUS_AMBIENTE.EM_PRODUCAO,
    colaboradorPlanilhaId: colaboradorDaPlanilha(demanda.row, indice),
    tarefas: tarefas.filter(tarefaAberta),
    pecasFora: (chave && indice.pecasForaPorAmbiente.get(chave)) || 0,
  };
}

/**
 * Cada demanda da planilha (`derivarDemandas`/`derivarSituacao` de
 * `setores.js`) enriquecida com o estado do app. `opcoes` = {catalogo, hoje};
 * sem `catalogo`, ele é montado do próprio `estado.setores`.
 *
 * `prazo`, `atrasada` e `emProducao` passam a ser os EFETIVOS (os helpers de
 * `setores.js` — `resumirMapa`, `blocosDoSetor` — continuam funcionando com o
 * resultado); os valores só da planilha ficam em `prazoPlanilha`,
 * `atrasadaPlanilha`, `emProducaoPlanilha`, `setorIdPlanilha` e
 * `setorNomePlanilha`.
 */
export function combinarDemandas(demandasDaPlanilha, estado, opcoes = {}) {
  if (!Array.isArray(demandasDaPlanilha)) return [];
  const { catalogo = criarCatalogoSetores(estado?.setores), hoje = new Date() } = opcoes;
  const indice = indexarEstado(estado);
  return demandasDaPlanilha.map((demanda) => combinarUma(demanda, indice, catalogo, hoje));
}

// Onde a demanda está: o setor, ou a bandeja (sem setor / obra futura).
const lugarDaDemanda = (d) => (d.situacao === SITUACAO.SETOR ? d.setorId : d.situacao);

function locaisPorChave(demandas) {
  const mapa = new Map();
  demandas.forEach((d) => {
    if (d.chave && !mapa.has(d.chave)) mapa.set(d.chave, { local: lugarDaDemanda(d), rotulo: d.rotulo });
  });
  return mapa;
}

/**
 * Ambientes que mudaram de lugar entre duas cargas da planilha (o PPCP digitou
 * a data e o ambiente "andou"), para o mapa animar a caminhada:
 * [{chave, rotulo, de, para}] — `de`/`para` = id do setor, "sem_setor" ou
 * "obra_futura"; `para: null` = saiu do mapa (carregado); `de: null` = entrou
 * agora. Sem carga anterior (lista vazia ou nula), nada andou.
 * Usa a `chave` do ambiente (estável), não a posição da linha.
 */
export function movimentosEntreSetores(anteriores, atuais) {
  if (!Array.isArray(anteriores) || anteriores.length === 0 || !Array.isArray(atuais)) return LISTA_VAZIA;
  const antes = locaisPorChave(anteriores);
  const depois = locaisPorChave(atuais);
  const movimentos = [];
  depois.forEach(({ local, rotulo }, chave) => {
    const de = antes.has(chave) ? antes.get(chave).local : null;
    if (de !== local) movimentos.push({ chave, rotulo, de, para: local });
  });
  antes.forEach(({ local, rotulo }, chave) => {
    if (!depois.has(chave)) movimentos.push({ chave, rotulo, de: local, para: null });
  });
  return movimentos.length ? movimentos : LISTA_VAZIA;
}
